import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Bell, Check, CheckCheck } from 'lucide-react';
import { api } from '../../lib/api';
import { NotificationItem } from '../../components/notifications';
import type { Notification } from '../../hooks/useNotifications';

type FilterType = 'all' | 'unread' | 'read';

interface NotificationsResponse {
  notifications: Notification[];
  unreadCount: number;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export default function Notifications() {
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<FilterType>('all');
  const [page, setPage] = useState(1);
  const limit = 20;

  const { data, isLoading } = useQuery({
    queryKey: ['notifications-page', { filter, page }],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('limit', String(limit));
      if (filter === 'unread') params.set('unreadOnly', 'true');
      const response = await api.get(`/users/notifications?${params}`);
      return (response as any).data as NotificationsResponse;
    },
  });

  const markAsReadMutation = useMutation({
    mutationFn: (notificationId: string) =>
      api.put(`/users/notifications/${notificationId}/read`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications-page'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const markAllAsReadMutation = useMutation({
    mutationFn: () => api.put('/users/notifications/read-all'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications-page'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const notifications = data?.notifications || [];
  const unreadCount = data?.unreadCount || 0;
  const pagination = data?.pagination;

  // Filter notifications if showing "read" only
  const filteredNotifications = filter === 'read'
    ? notifications.filter(n => n.readAt)
    : notifications;

  const filters: { id: FilterType; label: string; count?: number }[] = [
    { id: 'all', label: 'All' },
    { id: 'unread', label: 'Unread', count: unreadCount },
    { id: 'read', label: 'Read' },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-western text-3xl text-cream">Notifications</h1>
          {unreadCount > 0 && (
            <p className="text-text-secondary mt-1">
              You have {unreadCount} unread notification{unreadCount !== 1 ? 's' : ''}
            </p>
          )}
        </div>
        {unreadCount > 0 && (
          <button
            onClick={() => markAllAsReadMutation.mutate()}
            disabled={markAllAsReadMutation.isPending}
            className="btn-secondary flex items-center gap-2"
          >
            <CheckCheck className="w-4 h-4" />
            {markAllAsReadMutation.isPending ? 'Marking...' : 'Mark all as read'}
          </button>
        )}
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-1 mb-6 border-b border-gold/20">
        {filters.map(f => (
          <button
            key={f.id}
            onClick={() => {
              setFilter(f.id);
              setPage(1);
            }}
            className={`px-4 py-3 font-medium transition-colors relative flex items-center gap-2 ${
              filter === f.id
                ? 'text-gold'
                : 'text-text-secondary hover:text-cream'
            }`}
          >
            {f.label}
            {f.count !== undefined && f.count > 0 && (
              <span className="px-1.5 py-0.5 text-xs bg-gold/20 text-gold rounded-full">
                {f.count}
              </span>
            )}
            {filter === f.id && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gold" />
            )}
          </button>
        ))}
      </div>

      {/* Notifications List */}
      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="card p-4 animate-pulse">
              <div className="flex gap-3">
                <div className="w-10 h-10 bg-leather rounded-full" />
                <div className="flex-1">
                  <div className="h-5 bg-leather rounded w-1/3 mb-2" />
                  <div className="h-4 bg-leather rounded w-2/3" />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : filteredNotifications.length === 0 ? (
        <div className="card p-12 text-center">
          <Bell className="w-16 h-16 text-text-muted/30 mx-auto mb-4" />
          <h2 className="text-xl text-cream mb-2">
            {filter === 'unread' ? 'No unread notifications' : 'No notifications yet'}
          </h2>
          <p className="text-text-secondary">
            {filter === 'unread'
              ? "You're all caught up!"
              : "We'll notify you about important updates like order completions, support replies, and more."}
          </p>
        </div>
      ) : (
        <>
          <div className="card divide-y divide-gold/10 overflow-hidden">
            {filteredNotifications.map(notification => (
              <div key={notification.id} className="relative group">
                <NotificationItem
                  notification={notification}
                  onMarkAsRead={(id) => markAsReadMutation.mutate(id)}
                  showFullMessage
                />
                {!notification.readAt && (
                  <button
                    onClick={() => markAsReadMutation.mutate(notification.id)}
                    className="absolute top-3 right-3 p-1.5 text-text-muted hover:text-gold opacity-0 group-hover:opacity-100 transition-opacity rounded-lg hover:bg-surface"
                    title="Mark as read"
                  >
                    <Check className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <div className="flex items-center justify-between mt-6">
              <p className="text-text-muted text-sm">
                Showing {((page - 1) * limit) + 1} - {Math.min(page * limit, pagination.total)} of {pagination.total}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-3 py-1.5 text-sm bg-surface hover:bg-gold/10 text-text-secondary hover:text-cream disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))}
                  disabled={page === pagination.totalPages}
                  className="px-3 py-1.5 text-sm bg-surface hover:bg-gold/10 text-text-secondary hover:text-cream disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
